import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
}

interface RefreshResponse {
  access_token: string;
  refresh_token?: string;
  token_type: 'bearer';
  expires_in: number;
}

let refreshPromise: Promise<string | null> | null = null;

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname + window.location.search;
  const excluded = ['/login', '/register', '/password-reset'];
  const alreadyOnAuthPage = excluded.some((p) => window.location.pathname.startsWith(p));
  const target = alreadyOnAuthPage
    ? '/login'
    : `/login?redirect=${encodeURIComponent(currentPath)}`;
  if (window.location.pathname + window.location.search !== target) {
    window.location.assign(target);
  }
}

async function performRefresh(client: AxiosInstance, refreshToken: string): Promise<string | null> {
  try {
    const response = await client.post<RefreshResponse>(
      '/auth/refresh',
      { refresh_token: refreshToken },
      { _skipAuthRefresh: true } as AxiosRequestConfig,
    );
    const auth = useAuthStore();
    auth.setAccessToken(response.data.access_token, response.data.expires_in);
    // If backend rotates refresh tokens, persist the new one via setAuth-like path.
    if (response.data.refresh_token && auth.user) {
      auth.setAuth({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        token_type: 'bearer',
        expires_in: response.data.expires_in,
        user: auth.user,
      });
    }
    return response.data.access_token;
  } catch {
    return null;
  }
}

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/api/v1',
    timeout: 20000,
    withCredentials: true,
  });

  client.interceptors.request.use((config) => {
    const typed = config as RetryableRequestConfig;
    const auth = useAuthStore();
    const token = auth.getAccessToken();
    if (token && !typed._skipAuthRefresh) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetryableRequestConfig | undefined;
      const status = error.response?.status;

      // Not a 401, or no config to retry, or already retried, or explicitly opted out
      if (
        status !== 401 ||
        !original ||
        original._retry ||
        original._skipAuthRefresh ||
        original.url?.includes('/auth/login') ||
        original.url?.includes('/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      const auth = useAuthStore();
      const refreshToken = auth.getRefreshToken();
      if (!refreshToken) {
        auth.clearAuth();
        redirectToLogin();
        return Promise.reject(error);
      }

      original._retry = true;

      if (refreshPromise === null) {
        refreshPromise = performRefresh(client, refreshToken).finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (!newToken) {
        auth.clearAuth();
        redirectToLogin();
        return Promise.reject(error);
      }

      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return client.request(original);
    },
  );

  return client;
}

export const apiClient = createClient();

// augment Axios config typing locally for _skipAuthRefresh
declare module 'axios' {
  export interface AxiosRequestConfig {
    _skipAuthRefresh?: boolean;
  }
}
